import React, { useState, useEffect } from "react";
import axios from "axios";
import NotificationModal from "../components/NotificationModal";
import ErrorModal from "../components/ErrorModal";

const SuperAdminConsole = () => {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    role: "",
  });
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [notification, setNotification] = useState({
    isOpen: false,
    message: "",
    modalType: "success",
  });
  const [error, setError] = useState({ show: false, message: "" });
  const [logs, setLogs] = useState([]);
  const [systemHealth, setSystemHealth] = useState({
    uptime: "",
    dbStatus: "",
  });
  const [settings, setSettings] = useState({
    emailNotifications: true,
    maintenanceMode: false,
  });

  //TODO: Change to backend api Url
  const apiUrl = "http://localhost:5000/";
//fetching all users from the backend
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${apiUrl}api/auth/all-users`);
        setUsers(response.data);
      } catch (error) {
        setError({ show: true, message: "Error fetching users" });
      }
    };

    fetchUsers();
    addLog("Fetched users from backend.");
  }, []);

  // Handle Input Change for Adding User
  const handleInputChange = (e) => {
    setNewUser({ ...newUser, [e.target.name]: e.target.value });
  };

  // Add a New User
  const addUser = async () => {
    try {
      const response = await axios.post(`${apiUrl}api/auth/register`, newUser);
      setUsers([...users, response.data.user]);
      setNewUser({ username: "", email: "", password: "", role: "" });
      setNotification({
        isOpen: true,
        message: "User added successfully",
        modalType: "success",
      });
    } catch (error) {
      setError({ show: true, message: "Error adding user" });
    }
  };

  // Delete a User
  const deleteUser = async (id) => {
    try {
      await axios.delete(`${apiUrl}api/auth/delete-user/${id}`);
      setUsers(users.filter((user) => user.id !== id));
      setNotification({
        isOpen: true,
        message: "User deleted successfully",
        modalType: "success",
      });
    } catch (error) {
      setError({ show: true, message: "Error deleting user" });
    }
  };

  // Open the Reset Password Modal
  const openResetPasswordModal = (id) => {
    setResetUserId(id);
  };

  // Reset Password for a User
  const resetPassword = async () => {
    try {
      await axios.put(`${apiUrl}api/auth/reset-password/${resetUserId}`, {
        newPassword,
      });
      setNotification({
        isOpen: true,
        message: "Password reset successfully",
        modalType: "success",
      });
      setResetUserId(null); // Close reset password modal
      setNewPassword("");
    } catch (error) {
      setError({ show: true, message: "Error resetting password" });
    }
  };

  // Add a Log Entry
  const addLog = (action) => {
    const newLog = {
      id: logs.length + 1,
      action,
      timestamp: new Date().toLocaleString(),
    };
    setLogs([...logs, newLog]);
  };

  // Toggle Email Notifications
  const toggleEmailNotifications = () => {
    setSettings({
      ...settings,
      emailNotifications: !settings.emailNotifications,
    });
    addLog(
      `Email notifications turned ${
        settings.emailNotifications ? "off" : "on"
      }.`
    );
  };

  // Toggle Maintenance Mode
  const toggleMaintenanceMode = () => {
    setSettings({ ...settings, maintenanceMode: !settings.maintenanceMode });
    addLog(
      `Maintenance mode turned ${settings.maintenanceMode ? "off" : "on"}.`
    );
  };

  // Fetch System Health (Simulated)
  useEffect(() => {
    setSystemHealth({
      uptime: "99.99%",
      dbStatus: "Operational",
    });
    addLog("System health fetched.");
  }, []);

  return (
    <div className="w-full border-2 px-4 bg-white shadow-lg pb-4 pt-4 rounded-md overflow-y-auto h-[calc(100vh-13.5rem)]">
      <h2 className="text-2xl font-bold text-center text-green-700 underline mb-4">
        Super Admin Console
      </h2>

      {/* Add New User */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-2">Add New User</h3>
        <input
          type="text"
          name="username"
          value={newUser.username}
          onChange={handleInputChange}
          placeholder="Username"
          className="border rounded p-2 mr-2"
        />
        <input
          type="email"
          name="email"
          value={newUser.email}
          onChange={handleInputChange}
          placeholder="Email"
          className="border rounded p-2 mr-2"
        />
        <input
          type="password"
          name="password"
          value={newUser.password}
          onChange={handleInputChange}
          placeholder="Password"
          className="border rounded p-2 mr-2"
        />
        <input
          type="text"
          name="role"
          value={newUser.role}
          onChange={handleInputChange}
          placeholder="Role (e.g., admin, hr, superadmin)"
          className="border rounded p-2 mr-2"
        />
        <button
          onClick={addUser}
          className="bg-green-600 text-white py-2 px-4 rounded"
        >
          Add User
        </button>
      </div>

      {/* User Table */}
      <table className="min-w-[70%] bg-white border border-gray-300 mb-6">
      <thead className="text-left">
          <tr>
            <th className="py-2 px-4 border-b">User ID</th>
            <th className="py-2 px-4 border-b">Username</th>
            <th className="py-2 px-4 border-b">Role</th>
            <th className="py-2 px-4 border-b">Actions</th>
          </tr>
        </thead>        
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td className="py-2 px-4 border-b">{user.id}</td>
              <td className="py-2 px-4 border-b">{user.username}</td>
              <td className="py-2 px-4 border-b">{user.role}</td>
              <td className="py-2 px-4 border-b">
                <button
                  onClick={() => openResetPasswordModal(user.id)}
                  className="bg-blue-500 text-white py-1 px-3 rounded mr-2"
                >
                  Reset Password
                </button>
                <button
                  onClick={() => deleteUser(user.id)}
                  className="bg-red-500 text-white py-1 px-3 rounded"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* System Settings */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-2">System Settings</h3>
        <button
          onClick={toggleEmailNotifications}
          className="bg-gray-500 text-white py-2 px-4 rounded mr-4"
        >
          Toggle Email Notifications:{" "}
          {settings.emailNotifications ? "On" : "Off"}
        </button>
        <button
          onClick={toggleMaintenanceMode}
          className="bg-gray-500 text-white py-2 px-4 rounded"
        >
          Toggle Maintenance Mode: {settings.maintenanceMode ? "On" : "Off"}
        </button>
      </div>

      {/* System Health */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-2">System Health</h3>
        <p>Uptime: {systemHealth.uptime}</p>
        <p>Database Status: {systemHealth.dbStatus}</p>
      </div>

      {/* Audit Logs */}
      <div>
        <h3 className="text-xl font-semibold mb-2">Audit Logs</h3>
        <table className="min-w-[70%] bg-white border border-gray-300">
          <thead className="text-left">
            <tr>
              <th className="py-2 px-4 border-b">Log ID</th>
              <th className="py-2 px-4 border-b">Action</th>
              <th className="py-2 px-4 border-b">Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id}>
                <td className="py-2 px-4 border-b">{log.id}</td>
                <td className="py-2 px-4 border-b">{log.action}</td>
                <td className="py-2 px-4 border-b">{log.timestamp}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Reset Password Modal */}
      {resetUserId && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow-lg">
            <h2 className="text-lg font-bold mb-4">Reset Password</h2>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New Password"
              className="border rounded p-2 mr-2"
            />
            <button
              onClick={resetPassword}
              className="bg-blue-500 text-white py-2 px-4 rounded mt-4"
            >
              Confirm Reset
            </button>
            <button
              onClick={() => setResetUserId(null)}
              className="mt-4 text-red-500"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Notification Modals */}
      <NotificationModal
        isOpen={notification.isOpen}
        message={notification.message}
        modalType={notification.modalType}
        onClose={() => setNotification({ ...notification, isOpen: false })}
      />
      <ErrorModal
        show={error.show}
        message={error.message}
        onClose={() => setError({ show: false, message: "" })}
      />
    </div>
  );
};

export default SuperAdminConsole;
